import { EmptyState, MMButton, PAGE_FILL, RecurringStatusBadge, TableCard, TransactionsIcon } from '@components'
import BackArrowIcon from '@icons/arrow-left.svg?react'
import cn from '@integrations/classnames'
import { useContext, useEffect, useState } from '@integrations/react'
import { getRecurringFrequencyDescription } from '@utils'
import { useLazyGetTransactionsQuery } from 'mmfintech-backend-api'
import type {
  PortalRecurringPlanView,
  TransactionHistoryFilterRequest,
  TransactionViewPortal
} from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, isValidArray, tr, usePagination } from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import { TRANSACTIONS_TABLE_SCROLL_CLASS } from '../TransactionsTable/TransactionsTable'
import RecurringPastTransactionDetailsModal from './RecurringPastTransactionDetailsModal'
import styles from './RecurringPastTransactions.module.scss'
import RecurringPastTransactionsTable from './RecurringPastTransactionsTable'

interface Props {
  plan: PortalRecurringPlanView
  onBack: () => void
}

function RecurringPastTransactions({ plan, onBack }: Props) {
  const { id, recurringPlanName, recurringPlanStatus, recurringStep, startDate, recurringAmount, currency, recipient } =
    plan || {}

  const { modalShow } = useContext(GlobalContext)
  const [ready, setReady] = useState(false)

  // Deliberately bypasses the useTransactionsFilterQry wrapper — its .reload() goes
  // through useFilter's own debounced executor, which left a window where this
  // component would render stale/empty results before the plan-filtered fetch actually
  // started. Driving useLazyGetTransactionsQuery directly (same pattern Kingdom's own
  // Recurring.tsx uses) fires the request synchronously inside the effect below, so
  // transactionsFetching flips true on the very next render with no visible flash.
  const [fetchTransactions, { transactions, transactionsError, transactionsFetching }] = useLazyGetTransactionsQuery({
    selectFromResult: ({ data, error, isFetching }) => ({
      transactions: data?.content,
      transactionsError: error,
      transactionsFetching: isFetching
    })
  })

  const pagination = usePagination({
    rowsPerPage: 10,
    reload: async (params, onSuccess) => {
      try {
        const response = await fetchTransactions({
          ...params,
          recurringPlanId: id,
          sort: 'reqTime-desc,id-desc'
        } as TransactionHistoryFilterRequest).unwrap()
        onSuccess?.(response)
      } catch {}
    }
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload only when the selected plan changes, not on every pagination identity change
  useEffect(() => {
    pagination.reload()
    setReady(true)
  }, [id])

  function handleRowClick(transaction: TransactionViewPortal) {
    modalShow({ content: <RecurringPastTransactionDetailsModal transaction={transaction} /> })
  }

  return (
    <div
      className={cn(
        styles['recurring-past-transactions-wrapper'],
        'recurring-past-transactions-wrapper',
        PAGE_FILL.item
      )}
      data-test='recurring-past-transactions'
    >
      <MMButton
        type='button'
        variant='dashboard'
        leftIcon={BackArrowIcon}
        caption={tr('FRONTEND.RECURRING.HISTORY.BACK', 'Back to recurring payments')}
        onClick={onBack}
        className={cn(styles['recurring-past-transactions-back-button'], 'recurring-past-transactions-back-button')}
        data-test='button-back-to-recurring'
      />

      <h3
        className={cn(styles['recurring-past-transactions-title'], 'recurring-past-transactions-title')}
        data-test='recurring-past-transactions-title'
      >
        {tr('FRONTEND.RECURRING.HISTORY.TITLE', 'Past transactions')} — {recurringPlanName}
      </h3>

      <div
        className={cn(styles['recurring-past-transactions-summary'], 'recurring-past-transactions-summary')}
        data-test='recurring-past-transactions-summary'
      >
        <RecurringStatusBadge status={recurringPlanStatus} />
        <span
          className={cn(styles['recurring-past-transactions-summary-item'], 'recurring-past-transactions-summary-item')}
        >
          {getRecurringFrequencyDescription(startDate, recurringStep)}
        </span>
        <span
          className={cn(styles['recurring-past-transactions-summary-item'], 'recurring-past-transactions-summary-item')}
        >
          ·
        </span>
        <span
          className={cn(styles['recurring-past-transactions-summary-item'], 'recurring-past-transactions-summary-item')}
        >
          {recipient}
        </span>
        <span
          className={cn(styles['recurring-past-transactions-summary-item'], 'recurring-past-transactions-summary-item')}
        >
          ·
        </span>
        <span
          className={cn(styles['recurring-past-transactions-summary-item'], 'recurring-past-transactions-summary-item')}
        >
          {formatMoney(recurringAmount, currency)}
        </span>
      </div>

      <ErrorDisplay error={transactionsError} />

      <TableCard
        fallback={
          !ready || transactionsFetching ? (
            <Spinner />
          ) : (
            !isValidArray(transactions) && (
              <EmptyState
                icon={<TransactionsIcon size={48} />}
                title={tr('FRONTEND.RECURRING.HISTORY.EMPTY', 'The first payment will appear here after it executes')}
                data-test='recurring-past-transactions-empty'
              />
            )
          )
        }
        pagination={pagination.register()}
        scrollProps={{
          className: cn(
            TRANSACTIONS_TABLE_SCROLL_CLASS,
            'table-cards-fit',
            'table-body-text-sm',
            'table-body-transparent',
            'table-body-line-tight'
          ),
          'data-test': 'recurring-past-transactions-table'
        }}
      >
        <RecurringPastTransactionsTable transactions={transactions} onRowClick={handleRowClick} />
      </TableCard>
    </div>
  )
}

export default RecurringPastTransactions
